// src/pages/LandingPage.js - Updated for invitation-based authentication
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApiCall } from '../hooks/useApiCall';
import apiService from '../services/apiService';
import ForgotPasswordModal from '../components/profile/ForgotPasswordModal';
import ResetPasswordForm from '../components/profile/ResetPasswordForm';
import { validatePassword } from '../utils/passwordValidation';

import '../styles/globals.css';

const LandingPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [forgotPasswordVariant, setForgotPasswordVariant] = useState(null); // null | 'reset' | 'invite'
  const [passwordError, setPasswordError] = useState(''); // ADD THIS
  const [inviteToken, setInviteToken] = useState(null);   // from ?mode=activate&token=...
  const [emailFromLink, setEmailFromLink] = useState(false);
  const [resetOobCode, setResetOobCode] = useState(null); // from ?mode=resetPassword&oobCode=...

  const { login } = useAuth(); // Remove signup from useAuth
  const navigate = useNavigate();
  const location = useLocation();
  
  const { loading, error, makeRequest, clearError } = useApiCall();

  /**
   * Handle password change with real-time validation
   */
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    // Only validate for signup/activation, not login
    if (!isLogin && newPassword) {
      const error = validatePassword(newPassword);
      setPasswordError(error);
    } else {
      setPasswordError('');
    }
  };

  // Handle URL parameters for activation and password-reset links.
  // Nothing here calls the API: tokens and reset codes are only used on submit,
  // so corporate link scanners opening the page don't consume them.
  // (Not logged: the URL carries single-use tokens.)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get('mode');

    if (mode === 'activate') {
      setIsLogin(false);
      setInviteToken(searchParams.get('token'));

      // Pre-fill email if provided in URL
      const emailParam = searchParams.get('email');
      if (emailParam) {
        setEmail(emailParam);
        setEmailFromLink(true);
      }
    } else if (mode === 'resetPassword') {
      setResetOobCode(searchParams.get('oobCode'));
    }
  }, [location.search]);

  const backToSignIn = () => {
    setResetOobCode(null);
    setInviteToken(null);
    setEmailFromLink(false);
    setIsLogin(true);
    navigate('/', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    try {
      if (isLogin) {
        // Login flow - authenticate with Firebase
        await makeRequest(
          async () => {
            const result = await login(email, password);
            if (!result.success) {
              throw new Error(result.error);
            }
            return result;
          },
          'Signing in...'
        );
        
        navigate('/app');
      } else {
        // Account activation flow - for invited users
        //  VALIDATE PASSWORD FIRST
        const passwordValidation = validatePassword(password);
        if (passwordValidation) {
          setPasswordError(passwordValidation);
          return; // Don't submit if password is invalid
        }
        
        // Call the new activation endpoint
        await makeRequest(
          async () => {
            console.log('Activating invited user account...');
            
            const userData = await apiService.publicPost('/api/v1/users/activate', {
              email: email,
              password: password,
              // Only a typed name: the API keeps the Admin's name, or falls back to the email prefix
              displayName: displayName.trim() || undefined,
              token: inviteToken
            });

            console.log('Account activated successfully:', userData);
            
            if (userData.success && userData.data.customToken) {
              const { signInWithCustomToken } = await import('firebase/auth');
              const { auth } = await import('../config/firebase');
              await signInWithCustomToken(auth, userData.data.customToken);
            }
            
            return userData;
          },
          'Activating your account...'
        );
        
        navigate('/dashboard');
      }
    } catch (authError) {
      console.error('Authentication error:', authError);

      const code = authError.responseData?.code || authError.message;

      if (code?.includes('FIREBASE_EMAIL_EXISTS')) {
        throw new Error('An account with this email already exists. Please use the sign in option.');
      } else if (authError.responseData?.error) {
        throw new Error(authError.responseData.error);
      }

      throw authError;
    }
  };

  return (
    <div className="landing-container">
      {/* Above-the-fold: hero left, auth right */}
      <div className="landing-above-fold">
        <section className="hero">
          <h1 className="hero-title">Better Conversations</h1>
          <h2 className="hero-subtitle">AI-Powered Communication Coaching</h2>
          <p className="hero-description">
            Master professional conversations with personalized AI coaching.
            Whether you&apos;re closing deals, giving feedback, or proposing goals,
            get expert guidance tailored to your specific situation.
          </p>
          <div className="hero-features-mini">
            <span>✓ Sales Conversations</span>
            <span>✓ Management Coaching</span>
            <span>✓ Personalized Guidance</span>
          </div>
        </section>

        {/* Auth Section */}
        <section className="auth-section">
          <div className="auth-container">
          {resetOobCode ? (
            <ResetPasswordForm
              oobCode={resetOobCode}
              onDone={backToSignIn}
              onRequestNewLink={() => setForgotPasswordVariant('reset')}
            />
          ) : (
          <div className="auth-form">
            <h3 className="auth-form-title">
              {isLogin ? 'Sign In' : 'Activate Your Account'}
            </h3>
            
            {!isLogin && (
              <div className="mb-4 p-3" style={{ 
                backgroundColor: '#eff6ff', 
                border: '1px solid #bfdbfe', 
                borderRadius: 'var(--radius-md)' 
              }}>
                <p className="text-sm" style={{ color: '#1d4ed8' }}>
                  {inviteToken ? (
                    <><strong>Welcome!</strong> Create your password to activate your account.</>
                  ) : (
                    <><strong>Have an invitation?</strong> Open the link in your invitation email to activate your
                    account. If you can&apos;t find it, or it has expired, we can send you a new one.</>
                  )}
                </p>
              </div>
            )}
            
            {error && (
              <div className="alert alert-error mb-4">
                {error}
                <button 
                  onClick={clearError}
                  className="alert-close"
                >
                  ×
                </button>
              </div>
            )}
            
            {!isLogin && !inviteToken ? (
              <button
                type="button"
                className="auth-button"
                onClick={() => setForgotPasswordVariant('invite')}
              >
                Send me a new invitation link
              </button>
            ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email {!isLogin && <span className="text-error">*</span>}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  readOnly={!isLogin && emailFromLink}
                  className="form-input"
                  placeholder={isLogin ? "your.email@company.com" : "Enter your invited email address"}
                />
                {!isLogin && (
                  <p className="text-xs text-gray-500 mt-1">
                    This must match the email address your administrator used to invite you.
                  </p>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password {!isLogin && <span className="text-error">*</span>}
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}  // UPDATED: Use new handler
                  required
                  disabled={loading}
                  minLength="8"  // UPDATED: Changed from 6 to 8
                  className="form-input"
                  placeholder={isLogin ? "Your password" : "Create a secure password (8+ characters)"}
                />
                
                {/* Dynamic password requirements checklist */}
                {!isLogin && (
                  <div className="password-requirements">
                    <p>Requirements:</p>
                    <ul>
                      <li className={password.length >= 8 ? 'valid' : ''}>8+ characters</li>
                      <li className={/[A-Z]/.test(password) ? 'valid' : ''}>Uppercase letter (A-Z)</li>
                      <li className={/[a-z]/.test(password) ? 'valid' : ''}>Lowercase letter (a-z)</li>
                      <li className={/[!@#$%^&*(),.?":{}|<>-]/.test(password) ? 'valid' : ''}>Special character (!@#$%...)</li>
                    </ul>
                  </div>
                )}
            
                {isLogin && (
                  <div className="text-right mt-2">
                    <button
                      type="button"
                      onClick={() => setForgotPasswordVariant('reset')}
                      className="link-button text-sm"
                      disabled={loading}
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}
              </div>
   
              {!isLogin && (
                <div className="form-group">
                  <label htmlFor="displayName" className="form-label">
                    Display Name (Optional)
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    disabled={loading}
                    className="form-input"
                    placeholder="How you'd like to be shown in the app"
                  />
                </div>
              )}
              
              <button 
                type="submit" 
                className="auth-button"
                disabled={loading || (!isLogin && passwordError)}  // UPDATED: Disable if password invalid
              >
                {loading ? (
                  <div className="flex items-center gap-2 justify-center">
                    <div className="loading-dots" style={{ scale: '0.7' }}>
                      <div className="loading-dot"></div>
                      <div className="loading-dot"></div>
                      <div className="loading-dot"></div>
                    </div>
                    <span>{isLogin ? 'Signing in...' : 'Activating account...'}</span>
                  </div>
                ) : (
                  isLogin ? 'Sign In' : 'Activate Account'
                )}
              </button>
            </form>
            )}
            
            <p className="auth-toggle">
              {isLogin ? "Have an invitation to activate? " : "Already have an active account? "}
              <button 
                type="button"
                className="link-button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setDisplayName('');
                  setPasswordError('');  // UPDATED: Clear password error
                  clearError();
                }}
                disabled={loading}
              >
                {isLogin ? 'Activate account' : 'Sign in'}
              </button>
            </p>
          </div>
          )}
        </div>
        </section>
      </div>

      <ForgotPasswordModal
        isOpen={forgotPasswordVariant !== null}
        variant={forgotPasswordVariant || 'reset'}
        onClose={() => setForgotPasswordVariant(null)}
      />
    </div>
  );
};

export default LandingPage;